import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface PolicyRow {
  readonly genaiPolicyId: string;
  readonly policyCode: string;
  readonly policyName: string;
  readonly policyType: string;
  readonly policyVersion: string;
  readonly status: string;
  readonly enforcement: string;
  readonly isActive: boolean;
}

const POLICY_TYPES = ['ACCEPTABLE_USE', 'DATA_HANDLING', 'CONTENT', 'PRIVACY'];
const ENFORCEMENTS = ['ADVISORY', 'MANDATORY'];
const STATUSES = ['', 'DRAFT', 'PUBLISHED', 'ARCHIVED'];

/**
 * M38 — GenAI Policy Console (GR_M36-M40 §L38.06 / §M row 13). The admin surface
 * over the governed GenAI policy master (T513C): governance officers author the
 * acceptable-use / data-handling / content policies that govern GenAI use in the
 * tenant, each carrying its verbatim policy_text for audit and a guarded
 * DRAFT → PUBLISHED → ARCHIVED lifecycle. Viewing needs POLICY_VIEW; authoring /
 * publishing / archiving / deleting needs POLICY_MANAGE (SoD). Being a governed
 * master it soft-deletes only (LAW 3) and is tenant-isolated server-side (LAW 4).
 * This screen backs the /admin/genai/policies route.
 */
export function GenaiPoliciesConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<PolicyRow[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [policyCode, setPolicyCode] = useState('');
  const [policyName, setPolicyName] = useState('');
  const [policyType, setPolicyType] = useState('ACCEPTABLE_USE');
  const [policyText, setPolicyText] = useState('');
  const [policyVersion, setPolicyVersion] = useState('1.0');
  const [enforcement, setEnforcement] = useState('ADVISORY');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    apiRequest<{ data: PolicyRow[] }>(`/v1/ai/policies?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    if (!policyCode.trim() || !policyName.trim() || !policyText.trim()) {
      setFormError(t('m38.policies.required'));
      return;
    }
    setBusy(true);
    try {
      await apiRequest<{ data: PolicyRow }>('/v1/ai/policies', {
        method: 'POST',
        body: {
          policyCode: policyCode.trim(),
          policyName: policyName.trim(),
          policyType,
          policyText: policyText.trim(),
          policyVersion: policyVersion.trim() || undefined,
          enforcement,
          description: description.trim() || undefined,
        },
        token,
      });
      setCreated(t('m38.policies.created', { code: policyCode.trim() }));
      setPolicyCode(''); setPolicyName(''); setPolicyText(''); setDescription('');
      setPolicyVersion('1.0');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setFormError(t('m38.policies.notAuthorised'));
      } else {
        setFormError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const lifecycle = async (r: PolicyRow, action: 'publish' | 'archive'): Promise<void> => {
    try {
      await apiRequest(`/v1/ai/policies/${r.genaiPolicyId}/${action}`, { method: 'POST', token });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggleActive = async (r: PolicyRow): Promise<void> => {
    try {
      await apiRequest(`/v1/ai/policies/${r.genaiPolicyId}`, {
        method: 'PATCH',
        body: { isActive: !r.isActive },
        token,
      });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const remove = async (r: PolicyRow): Promise<void> => {
    try {
      await apiRequest(`/v1/ai/policies/${r.genaiPolicyId}`, { method: 'DELETE', token });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <main aria-label="genai-policies-console">
      <h1>{t('m38.policies.title')}</h1>
      <p>{t('m38.policies.intro')}</p>

      <section aria-label="policy-create">
        <h2>{t('m38.policies.newHeading')}</h2>
        <form onSubmit={submit} aria-label="policy-form">
          <label>{t('m38.policies.policyCode')}
            <input aria-label="policy-code" value={policyCode} onChange={(e) => setPolicyCode(e.target.value)} />
          </label>
          <label>{t('m38.policies.policyName')}
            <input aria-label="policy-name" value={policyName} onChange={(e) => setPolicyName(e.target.value)} />
          </label>
          <label>{t('m38.policies.policyType')}
            <select aria-label="policy-type" value={policyType} onChange={(e) => setPolicyType(e.target.value)}>
              {POLICY_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m38.policies.policyText')}
            <textarea aria-label="policy-text" value={policyText} onChange={(e) => setPolicyText(e.target.value)} />
          </label>
          <label>{t('m38.common.version')}
            <input aria-label="policy-version" value={policyVersion} onChange={(e) => setPolicyVersion(e.target.value)} />
          </label>
          <label>{t('m38.policies.enforcement')}
            <select aria-label="enforcement" value={enforcement} onChange={(e) => setEnforcement(e.target.value)}>
              {ENFORCEMENTS.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m38.common.description')}
            <input aria-label="description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? t('m38.common.saving') : t('m38.policies.createBtn')}</Button>
        </form>
        {formError && <p role="alert">{formError}</p>}
        {created && <p aria-label="create-success">{created}</p>}
      </section>

      <section aria-label="policy-filters">
        <label>{t('m38.policies.filterByStatus')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m38.common.all')}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading
        ? <p>{t('common.loading')}</p>
        : (
          <DataTable<PolicyRow>
            ariaLabel="policy-table"
            testId="policy-table"
            rowKey={(r) => r.genaiPolicyId}
            rowAriaLabel={(r) => `policy-${r.policyCode}`}
            emptyMessage={t('m38.policies.noneDefined')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m38.common.code'), render: (r) => r.policyCode },
              { key: 'name', header: t('m38.common.name'), render: (r) => r.policyName },
              { key: 'type', header: t('m38.common.type'), render: (r) => r.policyType },
              { key: 'version', header: t('m38.common.version'), render: (r) => r.policyVersion },
              { key: 'status', header: t('m38.common.status'), render: (r) => r.status },
              { key: 'enforce', header: t('m38.policies.colEnforce'), render: (r) => r.enforcement },
              { key: 'active', header: t('m38.common.active'), render: (r) => r.isActive ? t('common.yes') : t('common.no') },
              { key: 'actions', header: t('m38.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.policyCode}`} onClick={() => lifecycle(r, 'publish')}>{t('m38.policies.publish')}</Button>
                  )}
                  {r.status === 'PUBLISHED' && (
                    <Button variant="danger" type="button" aria-label={`archive-${r.policyCode}`} onClick={() => lifecycle(r, 'archive')}>{t('m38.policies.archive')}</Button>
                  )}
                  <Button variant="secondary" type="button" aria-label={`toggle-${r.policyCode}`} onClick={() => toggleActive(r)}>
                    {r.isActive ? t('m38.common.disable') : t('m38.common.enable')}
                  </Button>
                  <Button variant="danger" type="button" aria-label={`delete-${r.policyCode}`} onClick={() => remove(r)}>{t('common.delete')}</Button>
                </>
              ) },
            ]}
          />
        )}
    </main>
  );
}
